'use client'

import { Check } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { forwardRef, useState } from 'react'
import { cn } from '~/lib/utils'

import { Button } from '~/modules/common/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/modules/common/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/modules/common/components/ui/popover'
import { ExpandIcon } from '~/modules/common/icons/expand'

interface ProvinceSelectorProps {
  value: string
  onChange: (province: string) => void
  provinces: string[]
  className?: string
  placeholderSelect?: string
  placeholderSearch?: string
  placeholderEmpty?: string
}

// Normalize text by removing accents and converting to lowercase
function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
}

export const ProvinceSelector = forwardRef<
  HTMLButtonElement,
  ProvinceSelectorProps
>(
  (
    {
      value,
      onChange,
      provinces,
      className,
      placeholderSelect,
      placeholderSearch,
      placeholderEmpty,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const t = useTranslations('common')

    React.useEffect(() => {
      if (value && provinces.length > 0 && !provinces.includes(value)) {
        onChange('')
      }
    }, [value, provinces, onChange])

    // Custom filter function that normalizes text for accent-insensitive search
    const filterFunction = (value: string, search: string) => {
      const normalizedValue = normalizeText(value)
      const normalizedSearch = normalizeText(search)
      return normalizedValue.includes(normalizedSearch) ? 1 : 0
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'flex gap-3 justify-between items-center px-6 py-3 w-full text-sm leading-none rounded-xl border border-black border-solid h-[50px] bg-white hover:bg-gray-50 transition-colors',
              className,
            )}
          >
            <span className="text-stone-900 truncate">
              {!value || (provinces.length > 0 && !provinces.includes(value))
                ? placeholderSelect
                || t('cart.selectProvince', {
                  defaultValue: 'Seleccionar provincia',
                })
                : value}
            </span>
            <ExpandIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command filter={filterFunction}>
            <CommandInput
              placeholder={
                placeholderSearch
                || t('cart.searchProvince', {
                  defaultValue: 'Buscar provincia...',
                })
              }
            />
            <CommandList>
              <CommandEmpty>
                {placeholderEmpty
                  || t('cart.noProvincesFound', {
                    defaultValue: 'No se encontraron provincias',
                  })}
              </CommandEmpty>
              <CommandGroup>
                {provinces.map(province => (
                  <CommandItem
                    key={province}
                    value={province}
                    onSelect={(currentValue) => {
                      onChange(currentValue === value ? '' : currentValue)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === province ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {province}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

ProvinceSelector.displayName = 'ProvinceSelector'
